import AudiotrackIcon from "@mui/icons-material/Audiotrack";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import BoltIcon from "@mui/icons-material/Bolt";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ImageIcon from "@mui/icons-material/Image";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import VisibilityIcon from "@mui/icons-material/Visibility";
import LabelIcon from "@mui/icons-material/Label";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import SendIcon from "@mui/icons-material/Send";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import ViewStreamIcon from "@mui/icons-material/ViewStream";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Collapse from "@mui/material/Collapse";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useTheme, alpha } from "@mui/material/styles";
import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";
import api from "../../services/api";

const panelWidth = 360;

function getMsgType(msg) {
  if (msg.type && msg.type !== "text") return msg.type;
  if (!msg.mediaPath) return "text";
  const ext = (msg.mediaPath || "").split(".").pop().toLowerCase();
  if (["mp3","ogg","wav","aac","m4a","opus"].includes(ext)) return "audio";
  if (["mp4","avi","mov","webm"].includes(ext)) return "video";
  if (["jpg","jpeg","png","gif","webp"].includes(ext)) return "image";
  return "file";
}

const TYPE_COLORS = {
  text:  "#1976d2",
  audio: "#00897b",
  image: "#0288d1",
  video: "#e65100",
  file:  "#6a1b9a",
};
const TYPE_ICONS = {
  text:  TextFieldsIcon,
  audio: AudiotrackIcon,
  image: ImageIcon,
  video: VideoLibraryIcon,
  file:  AttachFileIcon,
};

const fetchAllQuickAnswers = async () => {
  let all = [];
  let page = 1;
  let hasMore = true;
  while (hasMore && page <= 100) {
    const r = await api.get("/quickAnswers", { params: { pageNumber: page } });
    all = all.concat(r.data.quickAnswers || []);
    hasMore = r.data.hasMore === true;
    page++;
  }
  return all;
};

const TYPE_TABS = [
  { key: "all",    label: "Todos",   Icon: ViewStreamIcon },
  { key: "text",   label: "Texto",   Icon: TextFieldsIcon },
  { key: "audio",  label: "Áudio",   Icon: AudiotrackIcon },
  { key: "video",  label: "Vídeo",   Icon: VideoLibraryIcon },
  { key: "image",  label: "Imagem",  Icon: ImageIcon },
  { key: "file",   label: "Arquivo", Icon: AttachFileIcon },
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const mediaUrl = (p) => p ? (p.startsWith("http") ? p : BACKEND_URL + "/public/" + p.split("/").map(encodeURIComponent).join("/")) : null;

const PreviewDialog = ({ msg, onClose, onSend }) => {
  if (!msg) return null;
  const type = getMsgType(msg);
  const url = mediaUrl(msg.mediaPath);
  return (
    <Dialog open={!!msg} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ pb: 1, fontWeight: 700, fontSize: "1rem" }}>{msg.shortcut || "Preview"}</DialogTitle>
      <DialogContent>
        {type === "text" && <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.7 }}>{msg.message || ""}</Typography>}
        {type === "image" && url && <Box component="img" src={url} alt="preview" sx={{ width: "100%", borderRadius: 2, maxHeight: 340, objectFit: "contain" }} />}
        {type === "audio" && url && <Box component="audio" controls src={url} sx={{ width: "100%", mt: 1 }} />}
        {type === "video" && url && <Box component="video" controls src={url} sx={{ width: "100%", borderRadius: 2, maxHeight: 300 }} />}
        {type === "file" && url && <Typography variant="body2" sx={{ color: "text.secondary" }}>{msg.mediaName || msg.mediaPath?.split("/").pop()}</Typography>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ borderRadius: 2, textTransform: "none" }}>Fechar</Button>
        <Button variant="contained" startIcon={<SendIcon />} onClick={() => { onSend(msg); onClose(); }}
          sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}>Enviar</Button>
      </DialogActions>
    </Dialog>
  );
};

const MsgRow = ({ msg, onSend, onPreview, isDark, primary }) => {
  const type = getMsgType(msg);
  const Icon = TYPE_ICONS[type] || TextFieldsIcon;
  const color = TYPE_COLORS[type] || primary;
  const isText = type === "text";

  return (
    <Box
      onClick={() => onPreview(msg)}
      sx={{
        display: "flex", alignItems: "center", gap: 1.25,
        px: 1.5, py: 0.9, cursor: "pointer", borderRadius: 2, mx: 1, mb: 0.5,
        transition: 'all 0.15s ease',
        border: '1px solid transparent',
        "&:hover": {
          backgroundColor: isDark ? alpha(color, 0.1) : alpha(color, 0.06),
          border: `1px solid ${alpha(color, 0.2)}`,
          "& .send-btn": { opacity: 1, transform: 'scale(1)' },
        },
      }}
    >
      <Box sx={{
        width: 30, height: 30, borderRadius: '8px',
        background: `linear-gradient(135deg, ${alpha(color, 0.2)}, ${alpha(color, 0.08)})`,
        border: `1px solid ${alpha(color, 0.2)}`,
        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
      }}>
        <Icon sx={{ fontSize: "0.85rem", color }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" noWrap sx={{ fontSize: "0.82rem", fontWeight: 600, letterSpacing: '-0.1px' }}>
          {msg.shortcut}
        </Typography>
        {isText && msg.message && (
          <Typography variant="caption" noWrap
            sx={{ color: "text.secondary", fontSize: "0.69rem", display: "block", opacity: 0.75 }}>
            {msg.message.slice(0, 55)}{msg.message.length > 55 ? "…" : ""}
          </Typography>
        )}
      </Box>
      <Box sx={{ display: "flex", gap: 0.4, flexShrink: 0 }}>
        {!isText && (
          <Tooltip title="Visualizar/ouvir">
            <IconButton size="small" onClick={e => { e.stopPropagation(); onPreview(msg); }}
              sx={{ opacity: 0.6, width: 24, height: 24, color: color, backgroundColor: alpha(color, 0.08), "&:hover": { opacity: 1, backgroundColor: alpha(color, 0.18) } }}>
              <VisibilityIcon sx={{ fontSize: "0.72rem" }} />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Enviar">
          <IconButton className="send-btn" size="small" onClick={e => { e.stopPropagation(); onSend(msg); }}
            sx={{ opacity: 0.5, transform: "scale(0.9)", transition: "all 0.15s ease", color: color, width: 26, height: 26,
              backgroundColor: alpha(color, 0.1), "&:hover": { backgroundColor: alpha(color, 0.2), opacity: 1 } }}>
            <SendIcon sx={{ fontSize: "0.75rem" }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

const STEP_TYPE_LABELS = { text: "Texto", audio: "Áudio", image: "Imagem", video: "Vídeo", file: "Arquivo", label: "Etiqueta" };
const STEP_TYPE_COLORS = { text: "#1976d2", audio: "#00897b", image: "#0288d1", video: "#e65100", file: "#6a1b9a", label: "#388e3c" };

const ScriptRow = ({ script, onExecute, isDark }) => {
  const [expanded, setExpanded] = React.useState(false);
  const [stepPreview, setStepPreview] = React.useState(null);
  const count = script.messages?.length ?? 0;
  const hasTrigger = script.triggers?.length > 0;
  const color = "#ff6d00";
  const tags = (script.messages || []).filter(m => m.type === "label" && m.tag);
  const uniqueTags = tags.reduce((acc, m) => { if (!acc.find(t => t.id === m.tag.id)) acc.push(m.tag); return acc; }, []);

  return (
    <Box sx={{ mx: 1, mb: 0.5 }}>
      <Box sx={{
        display: "flex", alignItems: "center", gap: 1,
        px: 1.5, py: 1, borderRadius: expanded ? "8px 8px 0 0" : 2,
        border: `1px solid ${alpha(color, expanded ? 0.4 : 0.15)}`,
        borderBottom: expanded ? "none" : undefined,
        backgroundColor: isDark ? alpha(color, expanded ? 0.1 : 0.04) : alpha(color, expanded ? 0.07 : 0.03),
        transition: "all 0.15s ease",
        "&:hover": { borderColor: alpha(color, 0.4), backgroundColor: isDark ? alpha(color, 0.1) : alpha(color, 0.07) },
      }}>
        <Box sx={{ width: 30, height: 30, borderRadius: "8px", flexShrink: 0,
          background: `linear-gradient(135deg, ${alpha(color, 0.25)}, ${alpha(color, 0.1)})`,
          border: `1px solid ${alpha(color, 0.25)}`,
          display: "flex", alignItems: "center", justifyContent: "center" }}>
          <BoltIcon sx={{ fontSize: "0.9rem", color }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, cursor: "pointer" }} onClick={() => setExpanded(e => !e)}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography variant="body2" noWrap sx={{ fontSize: "0.82rem", fontWeight: 700 }}>{script.name}</Typography>
            {hasTrigger && <Tooltip title="Tem gatilho automático"><BoltIcon sx={{ fontSize: "0.65rem", color, flexShrink: 0 }} /></Tooltip>}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap", mt: 0.2 }}>
            {count > 0 && <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.68rem", opacity: 0.75 }}>{count} etapa{count !== 1 ? "s" : ""}</Typography>}
            {uniqueTags.map(tag => (
              <Box key={tag.id} sx={{ display: "flex", alignItems: "center", gap: 0.3, px: 0.6, py: 0.1, borderRadius: 1,
                backgroundColor: alpha(tag.color || "#388e3c", 0.15), border: `1px solid ${alpha(tag.color || "#388e3c", 0.4)}` }}>
                <LabelIcon sx={{ fontSize: "0.6rem", color: tag.color || "#388e3c" }} />
                <Typography variant="caption" sx={{ fontSize: "0.6rem", color: tag.color || "#388e3c", fontWeight: 600 }}>{tag.name}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
        <Tooltip title={expanded ? "Fechar etapas" : "Ver etapas"}>
          <IconButton size="small" onClick={() => setExpanded(e => !e)}
            sx={{ color, width: 24, height: 24, opacity: 0.7, "&:hover": { opacity: 1, backgroundColor: alpha(color, 0.1) } }}>
            {expanded ? <KeyboardArrowDownIcon sx={{ fontSize: "0.9rem" }} /> : <ChevronRightIcon sx={{ fontSize: "0.9rem" }} />}
          </IconButton>
        </Tooltip>
        <Tooltip title="Executar script">
          <IconButton size="small" onClick={e => { e.stopPropagation(); onExecute(script); }}
            sx={{ color: "#fff", flexShrink: 0, width: 28, height: 28, borderRadius: "8px",
              background: `linear-gradient(135deg, ${color}, #ff8f00)`,
              boxShadow: `0 2px 6px ${alpha(color, 0.4)}`,
              "&:hover": { boxShadow: `0 3px 10px ${alpha(color, 0.55)}`, transform: "scale(1.05)" } }}>
            <PlayArrowIcon sx={{ fontSize: "0.85rem" }} />
          </IconButton>
        </Tooltip>
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ border: `1px solid ${alpha(color, 0.4)}`, borderTop: "none", borderRadius: "0 0 8px 8px",
          backgroundColor: isDark ? alpha(color, 0.03) : alpha(color, 0.02), pb: 0.5 }}>
          {(script.messages || []).map((m, i) => {
            const stepColor = STEP_TYPE_COLORS[m.type] || "#666";
            const stepUrl = m.mediaPath ? ((process.env.REACT_APP_BACKEND_URL || "") + (m.mediaPath.startsWith("script-") ? "/public/uploads/scripts/" : "/public/") + m.mediaPath) : null;
            const hasMedia = ["audio","image","video","file"].includes(m.type) && stepUrl;
            return (
              <Box key={m.id || i} sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 0.6,
                borderBottom: i < (script.messages.length - 1) ? `1px solid ${alpha(stepColor, 0.08)}` : "none" }}>
                <Typography variant="caption" sx={{ color: "text.disabled", fontSize: "0.65rem", minWidth: 14, textAlign: "center" }}>{i + 1}</Typography>
                <Box sx={{ width: 20, height: 20, borderRadius: "6px", backgroundColor: alpha(stepColor, 0.15),
                  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  {m.type === "label" ? <LabelIcon sx={{ fontSize: "0.7rem", color: stepColor }} /> : <BoltIcon sx={{ fontSize: "0.7rem", color: stepColor }} />}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {m.type === "label" && m.tag ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: m.tag.color || "#388e3c", flexShrink: 0 }} />
                      <Typography variant="caption" noWrap sx={{ fontSize: "0.7rem", fontWeight: 600, color: m.tag.color || "#388e3c" }}>{m.tag.name}</Typography>
                    </Box>
                  ) : m.type === "text" ? (
                    <Typography variant="caption" noWrap sx={{ fontSize: "0.7rem", color: "text.secondary" }}>{m.content ? m.content.slice(0, 45) : "(sem texto)"}</Typography>
                  ) : (
                    <Typography variant="caption" noWrap sx={{ fontSize: "0.7rem", color: "text.secondary" }}>{STEP_TYPE_LABELS[m.type] || m.type}</Typography>
                  )}
                </Box>
                {m.delaySeconds > 0 && (
                  <Typography variant="caption" sx={{ fontSize: "0.62rem", color: "text.disabled", flexShrink: 0 }}>{m.delaySeconds}s</Typography>
                )}
                {hasMedia && (
                  <Tooltip title={m.type === "audio" ? "Ouvir" : m.type === "image" ? "Ver imagem" : "Visualizar"}>
                    <IconButton size="small" onClick={() => setStepPreview({ ...m, _stepUrl: stepUrl })}
                      sx={{ color: stepColor, width: 22, height: 22, opacity: 0.75, "&:hover": { opacity: 1, backgroundColor: alpha(stepColor, 0.1) } }}>
                      <VisibilityIcon sx={{ fontSize: "0.75rem" }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            );
          })}
        </Box>
      </Collapse>

      {stepPreview && (
        <Dialog open={!!stepPreview} onClose={() => setStepPreview(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
          <DialogTitle sx={{ pb: 1, fontWeight: 700, fontSize: "1rem", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            {STEP_TYPE_LABELS[stepPreview.type] || stepPreview.type}
            <IconButton size="small" onClick={() => setStepPreview(null)}><CloseIcon fontSize="small" /></IconButton>
          </DialogTitle>
          <DialogContent>
            {stepPreview.type === "image" && stepPreview._stepUrl && <Box component="img" src={stepPreview._stepUrl} alt="preview" sx={{ width: "100%", borderRadius: 2, maxHeight: 340, objectFit: "contain" }} />}
            {stepPreview.type === "audio" && stepPreview._stepUrl && <Box component="audio" controls src={stepPreview._stepUrl} sx={{ width: "100%", mt: 1 }} />}
            {stepPreview.type === "video" && stepPreview._stepUrl && <Box component="video" controls src={stepPreview._stepUrl} sx={{ width: "100%", borderRadius: 2, maxHeight: 300 }} />}
            {stepPreview.type === "file" && stepPreview._stepUrl && <Box sx={{ textAlign: "center", py: 2 }}><Button variant="outlined" href={stepPreview._stepUrl} target="_blank">{stepPreview.mediaName || "Baixar arquivo"}</Button></Box>}
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
};


const CategoryAccordion = ({ name, items, renderItem, isDark, defaultOpen }) => {
  const [open, setOpen] = useState(defaultOpen || false);

  return (
    <Box sx={{ mb: 0.5 }}>
      <Box
        onClick={() => setOpen(v => !v)}
        sx={{
          display: "flex", alignItems: "center",
          px: 2, py: 0.9, cursor: "pointer", userSelect: "none",
          borderRadius: 1,
          mx: 1,
          transition: 'background 0.15s ease',
          "&:hover": { backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)" },
        }}
      >
        <Typography variant="body2" sx={{ flex: 1, fontWeight: 700, fontSize: "0.8rem", letterSpacing: '0.2px', textTransform: 'uppercase', opacity: 0.6 }}>
          {name}
        </Typography>
        <Box sx={{
          px: 1, py: 0.15, borderRadius: 10,
          backgroundColor: isDark ? "rgba(255,255,255,0.1)" : "rgba(25,118,210,0.1)",
          color: isDark ? 'rgba(255,255,255,0.7)' : '#1565c0',
          fontSize: '0.62rem', fontWeight: 700, mr: 0.75,
        }}>
          {items.length}
        </Box>
        {open
          ? <ExpandLessIcon sx={{ fontSize: "0.9rem", color: "text.secondary" }} />
          : <ExpandMoreIcon sx={{ fontSize: "0.9rem", color: "text.secondary" }} />}
      </Box>
      <Collapse in={open}>
        <Box sx={{ pb: 0.5 }}>
          {items.map(renderItem)}
        </Box>
      </Collapse>
    </Box>
  );
};

const Empty = ({ text }) => (
  <Box sx={{ textAlign: "center", pt: 5, px: 3 }}>
    <Box sx={{
      width: 52, height: 52, borderRadius: '16px', mx: 'auto', mb: 1.5,
      background: 'linear-gradient(135deg, rgba(25,118,210,0.12), rgba(25,118,210,0.04))',
      border: '1px solid rgba(25,118,210,0.15)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <SearchIcon sx={{ fontSize: "1.4rem", color: "rgba(25,118,210,0.4)" }} />
    </Box>
    <Typography variant="body2" sx={{ fontSize: "0.8rem", fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
      {text}
    </Typography>
    <Typography variant="caption" sx={{ fontSize: "0.7rem", color: 'text.disabled' }}>
      Tente ajustar os filtros
    </Typography>
  </Box>
);

const MessagesScriptsPanel = ({ open, onClose, ticketId }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const primary = theme.palette.primary.main;

  const [typeTab, setTypeTab]     = useState("all");
  const [messages, setMessages]   = useState([]);
  const [scripts, setScripts]     = useState([]);
  const [products, setProducts]   = useState([]);

  const [loading, setLoading]     = useState(false);
  const [search, setSearch]       = useState("");
  const [filterProduct, setFilterProduct] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [previewMsg, setPreviewMsg]         = useState(null);

  const cacheRef    = useRef({ loaded: false, messages: [], scripts: [], products: [] });
  const hasOpenedRef = useRef(false);

  const loadData = useCallback(async (force) => {
    if (cacheRef.current.loaded && !force) {
      setMessages(cacheRef.current.messages);
      setScripts(cacheRef.current.scripts || []);
      setProducts(cacheRef.current.products);
      return;
    }
    setLoading(true);
    try {
      const [msgs, srRes, prRes] = await Promise.all([
        fetchAllQuickAnswers(),
        api.get("/scripts"),
        api.get("/products"),
      ]);
      const scrs = srRes.data.scripts || srRes.data || [];
      const prds = prRes.data || [];
      cacheRef.current = { loaded: true, messages: msgs, scripts: scrs, products: prds };
      setMessages(msgs);
      setScripts(scrs);
      setProducts(prds);
    } catch (e) { toastError(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (!open) return;
    if (!hasOpenedRef.current) {
      hasOpenedRef.current = true;
      loadData(false);
    } else if (cacheRef.current.loaded) {
      setMessages(cacheRef.current.messages);
      setScripts(cacheRef.current.scripts || []);
      setProducts(cacheRef.current.products);
    }
  }, [open, loadData]);

  const handleRefresh = () => {
    cacheRef.current.loaded = false;
    loadData(true);
  };

  const handleSend = async (msg) => {
    try {
      if (getMsgType(msg) !== "text" && msg.mediaPath) {
        const encodedPath = msg.mediaPath.split("/").map(encodeURIComponent).join("/");
        const fetchUrl = BACKEND_URL + "/public/" + encodedPath;
        const response = await fetch(fetchUrl);
        if (!response.ok) {
          toastError({ message: "Arquivo não encontrado (" + response.status + "). Recadastre a Resposta Rápida." });
          return;
        }
        const blob = await response.blob();
        if (blob.size === 0) {
          toastError({ message: "Arquivo vazio. Recadastre a Resposta Rápida." });
          return;
        }
        const fileName = msg.mediaPath.split("/").pop() || "media";
        const file = new File([blob], fileName, { type: blob.type || "application/octet-stream" });
        const fd = new FormData();
        fd.append("fromMe", "true");
        fd.append("body", msg.message || "");
        fd.append("medias", file, fileName);
        await api.post("/messages/" + ticketId, fd);
      } else {
        await api.post("/messages/" + ticketId, { body: msg.message, fromMe: true, read: 1 });
      }
      toast.success("Mensagem enviada!");
    } catch (e) { toastError(e); }
  };

  const [executing, setExecuting] = useState(false);

  const handleExec = async (script) => {
    if (executing) return;
    setExecuting(true);
    try {
      await api.post("/scripts/" + script.id + "/execute/" + ticketId);
      toast.success("Script \"" + script.name + "\" iniciado!");
    } catch (e) { toastError(e); }
    finally { setExecuting(false); }
  };

  // Derive categories from loaded messages and scripts (no separate API call needed)
  const allCategories = useMemo(() => {
    const cats = [...new Set([
      ...messages.map(m => m.category),
      ...scripts.map(s => s.category)
    ].filter(Boolean))].sort((a, b) => a.localeCompare(b, "pt", { numeric: true }));
    return cats.map(name => ({ id: name, name }));
  }, [messages, scripts]);

  const filteredMessages = useMemo(() => {
    let list = messages;
    if (typeTab !== "all") list = list.filter(m => getMsgType(m) === typeTab);
    if (filterProduct) list = list.filter(m => String(m.productId) === String(filterProduct));
    if (filterCategory) {
      list = list.filter(m => m.category === filterCategory);
    }
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(m => (m.shortcut || "").toLowerCase().includes(q) || (m.message || "").toLowerCase().includes(q));
    }
    return [...list].sort((a, b) => (a.shortcut || "").localeCompare(b.shortcut || "", "pt", { numeric: true }));
  }, [messages, typeTab, filterProduct, filterCategory, search, allCategories]);

  const groupedMessages = useMemo(() => {
    const map = {};
    filteredMessages.forEach(m => {
      const k = m.category || "__none__";
      if (!map[k]) map[k] = [];
      map[k].push(m);
    });
    return Object.keys(map)
      .sort((a, b) => {
        if (a === "__none__") return 1;
        if (b === "__none__") return -1;
        return a.localeCompare(b, "pt", { numeric: true });
      })
      .map(k => ({ name: k === "__none__" ? "Sem categoria" : k, items: map[k] }));
  }, [filteredMessages]);

  const filteredScripts = useMemo(() => {
    let list = scripts;
    if (filterProduct) list = list.filter(s => String(s.productId) === String(filterProduct));
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(s => (s.name || "").toLowerCase().includes(q));
    }
    return [...list].sort((a, b) => (a.name || "").localeCompare(b.name || "", "pt", { numeric: true }));
  }, [scripts, filterProduct, search]);

  const bgColor = isDark ? "#0f1c2e" : "#f8fafc";
  const surfaceBg = isDark ? "#162032" : "#ffffff";
  const borderColor = isDark ? "rgba(255,255,255,0.07)" : "rgba(25,118,210,0.1)";
  const inputBg = isDark ? alpha('#fff', 0.05) : alpha('#fff', 0.9);

  if (!open) return null;

  return (
    <>
      <Box sx={{
        width: panelWidth, height: "100%",
        display: "flex", flexDirection: "column",
        backgroundColor: bgColor,
        borderLeft: `1px solid ${borderColor}`,
        overflow: "hidden",
      }}>
        {/* ── Header ── */}
        <Box sx={{
          background: isDark
            ? `linear-gradient(135deg, #0a1929 0%, #0d47a1 100%)`
            : `linear-gradient(135deg, #1565c0 0%, #1e88e5 100%)`,
          px: 2, py: 1.4,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          flexShrink: 0,
          boxShadow: `0 2px 12px ${alpha('#1565c0', 0.35)}`,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{
              width: 28, height: 28, borderRadius: '8px',
              background: 'rgba(255,255,255,0.15)',
              border: '1px solid rgba(255,255,255,0.25)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <BoltIcon sx={{ fontSize: '1rem', color: '#fff' }} />
            </Box>
            <Typography fontWeight={700} sx={{ fontSize: "0.92rem", color: '#fff', letterSpacing: '-0.2px' }}>
              Speed Mode
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <IconButton size="small" onClick={handleRefresh} disabled={loading}
              sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff' } }}>
              {loading ? <CircularProgress size={13} sx={{ color: '#fff' }} /> : <RefreshIcon sx={{ fontSize: "1rem" }} />}
            </IconButton>
            <IconButton size="small" onClick={onClose}
              sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff' } }}>
              <CloseIcon sx={{ fontSize: "1rem" }} />
            </IconButton>
          </Box>
        </Box>

        {/* ── Type filter tabs ── */}
        <Box sx={{ backgroundColor: surfaceBg, borderBottom: `1px solid ${borderColor}`, flexShrink: 0, px: 1, pt: 0.75 }}>
          <Tabs
            value={typeTab}
            onChange={(_, v) => setTypeTab(v)}
            variant="scrollable"
            scrollButtons={false}
            sx={{
              minHeight: 40,
              "& .MuiTab-root": {
                minHeight: 36, minWidth: 0, px: 1, py: 0.5,
                fontSize: "0.63rem", fontWeight: 600,
                textTransform: "none", gap: 0.25,
                flexDirection: "column", borderRadius: '8px',
                transition: 'all 0.15s ease',
                color: 'text.secondary',
                '&.Mui-selected': {
                  color: primary,
                  backgroundColor: alpha(primary, 0.08),
                },
              },
              "& .MuiTabs-indicator": {
                height: 2, borderRadius: 2,
                backgroundColor: primary,
                bottom: 2,
              },
            }}
          >
            {TYPE_TABS.map(t => (
              <Tab
                key={t.key}
                value={t.key}
                icon={<t.Icon sx={{ fontSize: "0.9rem" }} />}
                label={t.label}
              />
            ))}
          </Tabs>
        </Box>

        {/* ── Filters ── */}
        <Box sx={{
          px: 1.5, pt: 1.2, pb: 1,
          backgroundColor: surfaceBg,
          borderBottom: `1px solid ${borderColor}`,
          flexShrink: 0,
          display: "flex", flexDirection: "column", gap: 0.9,
        }}>
          <Box sx={{ display: "flex", gap: 0.9 }}>
            <FormControl size="small" sx={{ flex: 1 }}>
              <Select
                displayEmpty
                value={filterProduct}
                onChange={e => { setFilterProduct(e.target.value); setFilterCategory(""); }}
                sx={{
                  fontSize: "0.77rem", backgroundColor: inputBg, borderRadius: 2,
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: borderColor },
                }}
                renderValue={v => v
                  ? (products.find(p => String(p.id) === String(v))?.name || "Produto")
                  : <span style={{ color: "#9e9e9e", fontSize: '0.77rem' }}>Produto</span>
                }
              >
                <MenuItem value=""><em style={{ fontSize: '0.82rem' }}>Todos produtos</em></MenuItem>
                {products.map(p => (
                  <MenuItem key={p.id} value={String(p.id)} sx={{ fontSize: "0.82rem" }}>{p.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ flex: 1 }}>
              <Select
                displayEmpty
                value={filterCategory}
                onChange={e => setFilterCategory(e.target.value)}
                sx={{
                  fontSize: "0.77rem", backgroundColor: inputBg, borderRadius: 2,
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: borderColor },
                }}
                renderValue={v => {
                  const cat = allCategories.find(c => c.id === v || String(c.id) === String(v));
                  return cat ? cat.name : <span style={{ color: "#9e9e9e", fontSize: '0.77rem' }}>Categoria</span>;
                }}
              >
                <MenuItem value=""><em style={{ fontSize: '0.82rem' }}>Todas</em></MenuItem>
                {allCategories.map(c => (
                  <MenuItem key={c.id} value={c.id} sx={{ fontSize: "0.82rem" }}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <TextField
            size="small" fullWidth
            placeholder="Pesquisar mensagem ou script..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: "0.9rem", color: "text.disabled" }} />
                </InputAdornment>
              ),
              sx: {
                fontSize: "0.78rem", backgroundColor: inputBg, borderRadius: 2,
                '& .MuiOutlinedInput-notchedOutline': { borderColor: borderColor },
              },
            }}
          />
        </Box>

        {/* ── Content ── */}
        <Box sx={{ flex: 1, overflowY: "auto", py: 0.5 }}>
          {loading ? (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", pt: 6, gap: 1.5 }}>
              <CircularProgress size={28} sx={{ color: primary }} />
              <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.78rem' }}>
                Carregando mensagens...
              </Typography>
            </Box>
          ) : (
            <Box>
              {groupedMessages.length === 0 ? (
                <Empty text={
                  search || filterProduct || filterCategory
                    ? "Nenhuma mensagem encontrada"
                    : "Nenhuma mensagem cadastrada"
                } />
              ) : (
                groupedMessages.map((g, i) => (
                  <CategoryAccordion
                    key={g.name}
                    name={g.name}
                    items={g.items}
                    isDark={isDark}
                    defaultOpen={i === 0 && groupedMessages.length === 1}
                    renderItem={m => (
                      <MsgRow
                        key={m.id}
                        msg={m}
                        onSend={handleSend}
                        onPreview={setPreviewMsg}
                        isDark={isDark}
                        primary={primary}
                      />
                    )}
                  />
                ))
              )}
            </Box>
          )}

          {/* Scripts section */}
          {filteredScripts.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Box sx={{
                display: "flex", alignItems: "center", px: 2, py: 1,
                mx: 1, borderRadius: 2, mb: 0.75,
                background: isDark
                  ? `linear-gradient(135deg, ${alpha('#ff6d00', 0.15)}, ${alpha('#ff6d00', 0.05)})`
                  : `linear-gradient(135deg, ${alpha('#ff6d00', 0.1)}, ${alpha('#ff6d00', 0.03)})`,
                border: `1px solid ${alpha('#ff6d00', 0.2)}`,
              }}>
                <BoltIcon sx={{ fontSize: "0.9rem", color: "#ff6d00", mr: 0.75 }} />
                <Typography variant="caption" fontWeight={800} sx={{ flex: 1, color: "#ff6d00", fontSize: "0.72rem", letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                  Scripts · {filteredScripts.length}
                </Typography>
              </Box>
              <Box sx={{ pb: 1 }}>
                {filteredScripts.map(s => (
                  <ScriptRow key={s.id} script={s} onExecute={handleExec} isDark={isDark} />
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      <PreviewDialog msg={previewMsg} onClose={() => setPreviewMsg(null)} onSend={handleSend} />
    </>
  );
};

export default MessagesScriptsPanel;
